import { zodResolver } from "@hookform/resolvers/zod";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import SouthIcon from "@mui/icons-material/South";
import VerifiedIcon from "@mui/icons-material/Verified";
import { Box, Button, CircularProgress, Grid, Typography, InputAdornment } from "@mui/material";
import { t } from "i18next";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import useDialogWithId from "../../../hooks/useDialogWithId";
import { CustomerAddPersonDialog } from "../../../page/CustomerPage/CustomerAddPersonDialog";
import { ClientPassportLeadsTable } from "../../../page/LeadsPage/ClientPassportLeadsTable";
import { useCustomerById } from "../../../services/customersService";
import { useCorrectLeadPersonMutation } from "../../../services/leadsService";
import BasicDialog from "../../base/BasicDialog";
import { ClientPassportButton } from "../../buttons/ClientPassportButton";
import { ControlledTextField } from "../../form/ControlledTextField/ControlledTextField";
import { ClientConfirmDiscoveryDialog } from "./ClientConfirmDiscoveryDialog";
import { SearchOtherClientDialog } from "./SearchOtherClientDialog";
import { SearchTopPotentialDialog } from "./SearchTopPotentialClientsDialog";

import {AdditionalEmailsButton} from "../../buttons/AdditionalEmailsButton"
import {AdditionalPhonesButton} from "../../buttons/AdditionalPhonesButton"


export const clientPassportFormSchema = z.object({
    personId: z.string().optional(),
    customerId: z.string().optional(),
    name: z.string().optional(),
    surname: z.string().optional(),
    email: z.string().optional(),
    phone: z.string().optional(),
});

export function ClientDiscoveryDialog(props) {
    const { control, setValue, watch } = useForm({
        resolver: zodResolver(clientPassportFormSchema),
        defaultValues: {},
    });

    const { person } = props;
    const { control: leadCustomerControl} = useForm({
        defaultValues: {
            name: person.person_name,
            surname: person.person_surname,
            email: person.email.email_address,
            phone: person.phone.phone_number,
        },
    });

    const { mutate: correctLeadPerson } = useCorrectLeadPersonMutation();

    // Use customerId to fetch client data if provided
    const {
        data: customerData,
        isLoading,
        refetch,
    } = useCustomerById(props.customerId || "", {
        enabled: !!props.customerId, // Only fetch if customerId is provided
    });

    useEffect(() => {
        if (!customerData) {
            return;
        }

        setValue("personId", customerData.person?.id);
        setValue("customerId", props.customerId);
        setValue("name", customerData.person?.name);
        setValue("surname", customerData.person?.surname);
        setValue("email", customerData.person?.email.email_address || "");
        setValue("phone", customerData.person?.phone.phone_number || "");
    }, [customerData, setValue, props.customerId]);
    const formValues = watch();

    const customerhasRefusedClause = customerData?.has_refused_clause || false
    const customersAreEquals = useMemo(()=>{
        return (
            customerData?.person?.name === person.person_name && 
            customerData?.person?.surname === person.person_surname && 
            customerData?.person?.email.email_address === person.email.email_address && 
            (customerData?.person?.phone.phone_number || "").replace("+", "").replace(" ", "").replace(/^48/, "") === (person.phone.phone_number || "").replace("+", "").replace(" ", "").replace(/^48/, "")
        )
    },[person, customerData])
    const showCustomerDataNoMatchAlert = customerhasRefusedClause && !customersAreEquals
    const [
        openConfirmDiscovery,
        confirmDiscoveryId,
        onOpenConfirmDiscovery,
        onCloseConfirmDiscovery,
    ] = useDialogWithId();
    const {
        openDialog: openTopPotentialClientsDialog,
        closeDialog: closeTopPotentialClientsDialog,
    } = useDialogBetter();
    const { openDialog: openSearchOtherClientDialog, closeDialog: closeSearchOtherClientDialog } =
        useDialogBetter();
    const { openDialog: openAddNewCustomerDialog, closeDialog: closeAddNewCustomerDialog } =
        useDialogBetter();
    const {
        openDialog: openConfirmClientDiscoveryDialog,
        closeDialog: closeConfirmClientDiscoveryDialog,
    } = useDialogBetter();

    function handleAddNewCustomerPersonFromDiscovery() {
        openAddNewCustomerDialog({
            Component: CustomerAddPersonDialog,
            componentProps: {
                defaultData: props.defaultData,
                actualClauses: person.actual_clauses,
                onSubmitSuccess: (data) => {
                    if (data.person_id) {
                        // Add a setTimeout to ensure any previous dialog operations are complete

                        setTimeout(() => {
                            console.log("Opening dialog now...");
                            openConfirmClientDiscoveryDialog({
                                Component: ClientConfirmDiscoveryDialog,
                                componentProps: {
                                    title: t("clients.discovery.assignCaretaker"), // TODO try do it more transactionally, so exiting this dialog will not cancel the lead correction.
                                    onConfirm: (sellerData) => {
                                        correctLeadPerson({
                                            person: data.person_id,
                                            lead: props.leadId,
                                            seller: sellerData.sellerId,
                                        });
                                        closeConfirmClientDiscoveryDialog();
                                        closeAddNewCustomerDialog();
                                        props.onClose();
                                    },
                                    personId: formValues.personId,
                                    customerId: props.customerId,
                                    newCustomerId: data.id,
                                    newPersonId: data.person_id,
                                },
                            });
                        }, 100);
                    }
                },
            },
        });
    }

    return (
        <>
            {/* @ts-ignore */}
            <BasicDialog
                open={props.open}
                onClose={props.onClose}
                titleAlign={"center"}
                contentAlign={"center"}
                title={props.title}
                maxWidth={"lg"}
                showDialogActions
                headerComponent={
                    <Typography
                        variant={"subtitle1"}
                        sx={{ color: "text.secondary", mx: "auto", mb: 1 }}
                    >
                        {t("clients.discovery.systemRecognizedClient")}
                    </Typography>
                }
                titleSx={{
                    pb: 0,
                }}
            >
                <Grid container direction={"column"}>
                    <CustomerInfoRow control={leadCustomerControl} />
                    <DownArrowCentered />
                    {isLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                        <CustomerInfoRow
                            control={control}
                            personId={formValues.personId}
                            button={
                                <ClientPassportButton
                                    clientId={formValues.customerId || ""}
                                    clientType="customer"
                                />
                            }
                        />
                        {showCustomerDataNoMatchAlert && <div style={{width: "100%", textAlign: "center"}}>
                            <Typography
                                variant={"h7"}
                                color={"error"}
                            >
                                {`*${t("clients.discovery.customerDataNoMatchAlert")}`}
                            </Typography>
                        </div>}
                        </>
                    )}
                    <Grid
                        item
                        sx={{
                            p: 2,
                            py: 1,
                            bgcolor: "whitesmoke",
                            mt: 2,
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant={"subtitle1"}
                            sx={{ color: "var(--primary)", mx: "auto", mb: 1 }}
                        >
                            {t("clients.discovery.historicalLeads")}
                        </Typography>
                        <ClientPassportLeadsTable personId={formValues.personId || ""} />
                    </Grid>
                    <Grid container justifyContent={"flex-end"} spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={4.5}>
                            <Button
                                onClick={() => {
                                    openTopPotentialClientsDialog({
                                        Component: SearchTopPotentialDialog,
                                        componentProps: {
                                            title: t("clients.discovery.topPotentialClients"),
                                            personId: person.id,
                                            onCustomerSelected: (customer) => {
                                                closeTopPotentialClientsDialog();
                                                setValue(
                                                    "email",
                                                    customer.person?.email.email_address || "",
                                                );
                                                setValue("name", customer.person?.name);
                                                setValue("surname", customer.person?.surname);
                                                setValue(
                                                    "phone",
                                                    customer.person?.phone.phone_number || "",
                                                );
                                                setValue("customerId", customer.id);
                                                setValue("personId", customer.person?.id);
                                                refetch();
                                            },
                                        },
                                    });
                                }}
                                startIcon={<VerifiedIcon />}
                                variant={"outlined"}
                                color={"success"}
                            >
                                {t("clients.discovery.topPotentialClients")}
                            </Button>
                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                onClick={() => {
                                    openSearchOtherClientDialog({
                                        Component: SearchOtherClientDialog,
                                        componentProps: {
                                            title: t("clients.discovery.searchClient"),
                                            onCustomerSelected: (customer) => {
                                                console.log(customer);
                                                closeSearchOtherClientDialog();
                                                setValue(
                                                    "email",
                                                    customer.person?.email.email_address || "",
                                                );
                                                setValue("name", customer.person?.name);
                                                setValue("surname", customer.person?.surname);
                                                setValue(
                                                    "phone",
                                                    customer.person?.phone.phone_number || "",
                                                );
                                                setValue("customerId", customer.id);
                                                setValue("personId", customer.person?.id);
                                                refetch();
                                            },
                                        },
                                    });
                                }}
                                startIcon={<SearchIcon />}
                                variant={"outlined"}
                                color={"primary"}
                            >
                                {t("clients.discovery.findOtherClient")}
                            </Button>
                        </Grid>
                        <Grid item xs={3.5}>
                            <Button
                                startIcon={<AddCircleOutlineIcon />}
                                variant={"contained"}
                                color={"primary"}
                                onClick={handleAddNewCustomerPersonFromDiscovery}
                            >
                                {t("clients.discovery.addNewClient")}
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                fullWidth
                                onClick={() => {
                                    // @ts-ignore
                                    onOpenConfirmDiscovery(confirmDiscoveryId);
                                    openConfirmClientDiscoveryDialog({
                                        Component: ClientConfirmDiscoveryDialog,
                                        componentProps: {
                                            title: t("clients.discovery.assignCaretaker"),
                                            onConfirm: (data) => {
                                                correctLeadPerson({
                                                    person: formValues.personId || "",
                                                    lead: props.leadId,
                                                    seller: data.sellerId,
                                                });
                                                closeConfirmClientDiscoveryDialog();
                                                props.onClose();
                                            },
                                            personId: formValues.personId,
                                            customerId: formValues.customerId,
                                        },
                                    });
                                }}
                                startIcon={<CheckCircleIcon />}
                                variant={"contained"}
                                color={"success"}
                            >
                                {t("clients.discovery.confirmRecognition")}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </BasicDialog>
        </>
    );
}

const CustomerInfoRow = (props) => {
    const { control, button, personId } = props;

    return (
        <Grid container direction={"row"} spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Grid item xs={3}>
                <ControlledTextField
                    control={control}
                    name="name"
                    label={t("clients.discovery.firstName")}
                    props={{
                        InputProps: {
                            readOnly: true,
                            shrink: true
                        },
                    }}
                />
            </Grid>
            <Grid item xs={3}>
                <ControlledTextField
                    control={control}
                    name="surname"
                    label={t("clients.discovery.lastName")}
                    props={{
                        InputProps: {
                            readOnly: true,
                            shrink: true
                        },
                    }}
                />
            </Grid>
            <Grid item xs={3}>
                <ControlledTextField
                    control={control}
                    name="email"
                    label={t("clients.discovery.email")}
                    props={{
                        InputProps: {
                            readOnly: true,
                            shrink: true,
                            endAdornment: personId ? (
                            <InputAdornment position="end">
                                <AdditionalEmailsButton personId={personId}/>
                            </InputAdornment>
                            ) : null
                        },
                    }}
                />
            </Grid>
            <Grid item xs={2}>
                <ControlledTextField
                    control={control}
                    name="phone"
                    label={t("clients.discovery.phone")}
                    props={{
                        InputProps: {
                            readOnly: true,
                            shrink: true,
                            endAdornment: personId ? (
                            <InputAdornment position="end">
                                <AdditionalPhonesButton personId={personId}/>
                            </InputAdornment>
                            ) : null
                        },
                    }}
                />
            </Grid>
            <Grid item xs={1}>
                {button}
            </Grid>
        </Grid>
    );
};
const DownArrowCentered = () => (
    <Grid
        container
        direction={"row"}
        spacing={2}
        alignItems="center"
        justifyContent="center"
        sx={{ mt: 1 }}
    >
        <Grid item>
            <SouthIcon />
        </Grid>
    </Grid>
);
